import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ThemeConfig } from "antd";

/**
 * The three interchangeable looks for the eCRF page.
 *
 *   classic  - what the app has always rendered: patient card on top, section
 *              menu beside the form.
 *   rail     - "Split Rail": patient details and section nav combined into one
 *              dark left rail, form on a plain white page.
 *   hero     - "Hero Card": tinted page, hero banner carrying the patient
 *              identity, sections as pill tabs, form in a floating card.
 *
 * Only the page shell changes between them. FormView is not aware of the theme
 * at all - every difference in the form body is expressed as CSS scoped to the
 * `data-ecrf-view` attribute on the page root.
 */
export type EcrfViewId = "classic" | "rail" | "hero";

export const ECRF_VIEWS: { id: EcrfViewId; label: string; hint: string }[] = [
    { id: "classic", label: "Κλασική", hint: "Κάρτα ασθενή πάνω, μενού στο πλάι" },
    { id: "rail", label: "Πλαϊνή στήλη", hint: "Ασθενής και ενότητες σε σκούρα στήλη" },
    { id: "hero", label: "Κάρτα", hint: "Επικεφαλίδα ασθενή και ενότητες σε καρτέλες" },
];

/**
 * How a field's control sits relative to its label.
 *
 *   right - labels left, controls right-aligned in a shared column. Uniform
 *           edges make a long section scan as a single list. The default.
 *   left  - labels left, controls left-aligned directly after them. The
 *           original behaviour; ragged, but compact for short sections.
 *   above - label stacked over its control. Widest controls and the most
 *           uniform of all, at the cost of roughly double the height.
 */
export type EcrfAlignId = "right" | "left" | "above";

export const ECRF_ALIGNS: { id: EcrfAlignId; label: string; hint: string }[] = [
    { id: "right", label: "Δεξιά", hint: "Πεδία στοιχισμένα δεξιά σε κοινή στήλη" },
    { id: "left", label: "Αριστερά", hint: "Πεδία αμέσως μετά την ετικέτα" },
    { id: "above", label: "Κάτω", hint: "Ετικέτα πάνω από το πεδίο" },
];

const STORAGE_KEY = "ecrf.view";
const ALIGN_STORAGE_KEY = "ecrf.align";

const isViewId = (v: unknown): v is EcrfViewId =>
    v === "classic" || v === "rail" || v === "hero";

const isAlignId = (v: unknown): v is EcrfAlignId =>
    v === "right" || v === "left" || v === "above";

interface EcrfViewContextValue {
    view: EcrfViewId;
    setView: (v: EcrfViewId) => void;
    align: EcrfAlignId;
    setAlign: (v: EcrfAlignId) => void;
}

const EcrfViewContext = createContext<EcrfViewContextValue>({
    view: "classic",
    setView: () => { },
    align: "right",
    setAlign: () => { },
});

export const useEcrfView = () => useContext(EcrfViewContext);

export const EcrfViewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Persisted so an investigator's choice survives a reload - the page already
    // restores patient/section from the URL, and a layout that reset itself on
    // every refresh would undo that.
    const [view, setViewState] = useState<EcrfViewId>(() => {
        try {
            const stored = window.localStorage.getItem(STORAGE_KEY);
            return isViewId(stored) ? stored : "classic";
        }
        catch {
            return "classic";
        }
    });

    const [align, setAlignState] = useState<EcrfAlignId>(() => {
        try {
            const stored = window.localStorage.getItem(ALIGN_STORAGE_KEY);
            return isAlignId(stored) ? stored : "right";
        }
        catch {
            return "right";
        }
    });

    const setView = (v: EcrfViewId) => {
        setViewState(v);
        try { window.localStorage.setItem(STORAGE_KEY, v); } catch { }
    };

    const setAlign = (v: EcrfAlignId) => {
        setAlignState(v);
        try { window.localStorage.setItem(ALIGN_STORAGE_KEY, v); } catch { }
    };

    const value = useMemo(() => ({ view, setView, align, setAlign }), [view, align]);

    return (
        <EcrfViewContext.Provider value={value}>
            {children}
        </EcrfViewContext.Provider>
    );
};

/**
 * Per-view overrides layered on top of the app's base theme.
 *
 * Deliberately narrow: the brand palette, control heights and radii stay exactly
 * as configured in index.tsx, so the three views read as one product rather than
 * three. What changes is the surface treatment - how cards, menus and headers sit
 * on the page - which is what actually distinguishes the mockups.
 */
export const getViewTheme = (view: EcrfViewId): ThemeConfig => {
    switch (view) {
        case "rail":
            return {
                components: {
                    // The rail paints its own dark surface, so the menu inside it
                    // must not also draw a light background.
                    Menu: {
                        darkItemBg: "transparent",
                        darkSubMenuItemBg: "transparent",
                        darkPopupBg: "#1f2d2b",
                    },
                    Card: {
                        // Form card sits on plain white in this view - a border
                        // rather than a shadow keeps it flat.
                        boxShadowTertiary: "none",
                    },
                },
            };

        case "hero":
            return {
                components: {
                    Card: {
                        // The form card floats above a tinted page, so it earns a
                        // real shadow and a softer corner.
                        borderRadiusLG: 12,
                    },
                    Segmented: {
                        borderRadius: 999,
                        borderRadiusSM: 999,
                    },
                },
            };

        default:
            return {};
    }
};
